// Pack + clock + progress → what the diorama shows (MAP_DIORAMA §5). Pure, unit-tested.
import type { Climate, Pack, Place, WeatherCondition } from '../../shared/pack/schema'
import type { TripClock } from '../domain/tripClock'
import { stageStatuses } from '../domain/stages'
import type { FlagData, PinData, PinStatus } from './world/markers'
import type { Weather } from './world/weather'
import { GENERIC_SPOT, genericStandIn, JAPAN_GENERIC } from '../../shared/islands'
import { t } from '../strings/en'

const minutes = (hhmm: string) => {
  const [h, m] = hhmm.split(':').map(Number) as [number, number]
  return h * 60 + m
}

/**
 * 0 day · 1 dusk · 2 night, from the trip-timezone wall clock. Dusk spans sunset ± 45 min;
 * dawn (sunrise ± 30 min) uses the dusk palette too.
 */
export function todFor(time: string, daylight: Pack['meta']['daylight']): 0 | 1 | 2 {
  const t = minutes(time)
  const rise = minutes(daylight.sunrise)
  const set = minutes(daylight.sunset)
  if (t >= set - 45 && t < set + 45) return 1
  if (t >= rise - 30 && t < rise + 30) return 1
  if (t >= rise + 30 && t < set - 45) return 0
  return 2
}

export function wxFor(condition: WeatherCondition | undefined): Weather {
  return condition === 'snow' ? 1 : 0
}

/**
 * Typical weather where we are (or will be) this month, from the pack climatology. A stage with
 * no climate of its own (a wrapped mystery stage has no region) gets the trip-wide picture: the
 * mean of every region that month, so its temperature can't give the place away.
 */
export function climateFor(pack: Pick<Pack, 'meta' | 'itinerary'>, clock: TripClock): Climate | undefined {
  const stage = clock.phase === 'during' ? clock.stage : pack.itinerary.stages[0]
  const date = clock.phase === 'during' ? clock.today : pack.meta.startDate
  const month = date.slice(5, 7)
  const own = stage ? pack.meta.climatology[stage.regionId]?.[month] : undefined
  if (own) return own
  const all = Object.values(pack.meta.climatology).flatMap((m) => (m[month] ? [m[month]] : []))
  if (!all.length) return undefined
  const counts = new Map<WeatherCondition, number>()
  for (const c of all) counts.set(c.condition, (counts.get(c.condition) ?? 0) + 1)
  const condition = [...counts].sort((a, b) => b[1] - a[1])[0]![0]
  return { tempC: Math.round(all.reduce((s, c) => s + c.tempC, 0) / all.length), condition }
}

/**
 * Islands the Map may show: only those with a stage the viewer can see. Every way of changing
 * island (the switcher, a town tap on another island, a remembered island) goes through this.
 */
export function shownIslands(pack: Pick<Pack, 'itinerary'>, ids: readonly string[]): string[] {
  return ids.filter((id) => pack.itinerary.stages.some((s) => s.spot === id))
}

/**
 * The trip as the Map shows it: a stage outside Japan (`generic`, B3) stands on Castle Town until
 * the generic island is picked (PLACEHOLDER; the Trip set shows a plain slab for any spot that
 * isn't a built place: stage/trip/minis).
 */
export function mapView<P extends Pick<Pack, 'itinerary'>>(pack: P): P {
  if (!pack.itinerary.stages.some((s) => s.spot === GENERIC_SPOT)) return pack
  // B5: the built world island once there is one (shared/islands WORLD_GENERIC_BUILT), else Castle Town.
  const standIn = genericStandIn(JAPAN_GENERIC.town)
  return { ...pack, itinerary: { ...pack.itinerary, stages: pack.itinerary.stages.map((s) => (s.spot === GENERIC_SPOT ? { ...s, spot: standIn } : s)) } }
}

/** The island a phone starts on when it has none remembered (B8): the first visible stage's. */
export function firstIsland(pack: Pick<Pack, 'itinerary'>, ids: readonly string[]): string | undefined {
  return pack.itinerary.stages.find((s) => ids.includes(s.spot))?.spot
}

/**
 * The island a phone opens on when it has none remembered: the stage the family is in now (the
 * trip clock's stage), the first stage before the trip, the last one after it; when that stage's
 * island isn't offered (a wrapped stage), the first visible stage's (firstIsland).
 */
export function islandNow(pack: Pick<Pack, 'itinerary'>, clock: TripClock, ids: readonly string[]): string | undefined {
  const stages = pack.itinerary.stages
  // By index, so the stage comes from `pack` (the Map's view of the trip, mapView).
  const stage = stages[clock.phase === 'during' ? clock.stageIndex : clock.phase === 'pre' ? 0 : stages.length - 1]
  return stage && ids.includes(stage.spot) ? stage.spot : firstIsland(pack, ids)
}

const PRIORITY: Record<PinStatus, number> = { now: 0, next: 1, done: 2, later: 3 }

/**
 * One pin per island spot. Several stages can share a spot (two Niseko stays, Sapporo at
 * both ends): the most relevant one wins (now > next > done > later).
 */
export function pinsFor(pack: Pack, clock: TripClock): PinData[] {
  const stages = pack.itinerary.stages
  const status = stageStatuses(stages, clock)
  // Before the trip, the first stage is what's next.
  if (clock.phase === 'pre' && status[0]) status[0] = 'next'
  const bySpot = new Map<string, PinData>()
  stages.forEach((s, i) => {
    const st = status[i]!
    const current = bySpot.get(s.spot)
    if (current && PRIORITY[current.status] <= PRIORITY[st]) return
    // Every pin is a marker without words (the pill and stop bar say where): this is its name.
    const label = st === 'now' || st === 'next' ? t.map.stay(s.name) : st === 'done' ? t.map.stopDone(s.name) : t.map.stopLater(s.name)
    bySpot.set(s.spot, { spot: s.spot, stageId: s.id, status: st, label })
  })
  return [...bySpot.values()]
}

/** Want-to-go places as flags on the spot of a stage in their region. */
export function flagsFor(pack: Pack, wanted: Place[]): FlagData[] {
  return wanted.flatMap((p) => {
    const stage = pack.itinerary.stages.find((s) => s.regionId === p.regionId)
    return stage ? [{ placeId: p.id, spot: stage.spot, label: `Want: ${p.name}` }] : []
  })
}
